import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import JSZip from 'jszip';
import fileDownload from 'js-file-download';
import './camera.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import TabMenu from '../../../components/base/TabMenu/TabMenu';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import CameraViewTypeSelector from './CameraViewTypeSelector';
import Camera from './Camera';
import { allNodeEvent, createScreenshot, loadNodeState } from '../actions';
import notificationDecorator, {
  errorNotification,
  successNotification,
} from '../../Notifications/notificationDecorator';
import { getResponseError, nameBySave } from '../../../utils/utils';
import { getCamerasData } from '../../Settings/reducer';
import { getDashboardData } from '../reducer';

const NODE_STATE_RUNNING = 1;
const NODE_STATE_PAUSED = 2;

const getAllNodeUiState = nodeState => {
  const states = Object.values(nodeState || {})
    .map(state => Number(state && state.code))
    .filter(state => !Number.isNaN(state));

  if (!states.length) {
    return null;
  }

  if (states.some(state => state === NODE_STATE_RUNNING)) {
    return 1;
  }

  if (states.every(state => state === NODE_STATE_PAUSED)) {
    return 0;
  }

  return null;
};

export const calculateLimit = (width, height, wb, hb) => {
  const blockWidth1 = width / wb;
  const blockHeight1 = blockWidth1 * 0.5625;
  const blockHeight2 = height / hb;
  const blockWidth2 = blockHeight2 / 0.5625;
  if (blockHeight1 * hb > height) {
    return {
      width: blockWidth2, height: blockHeight2, viewportWidth: blockWidth2 * wb, viewportHeight: blockHeight2 * hb,
    };
  }
  return {
    width: blockWidth1, height: blockHeight1, viewportWidth: blockWidth1 * wb, viewportHeight: blockHeight1 * hb,
  };
};

export const calculateCamSize = (frameWidth, frameHeight, selectedViewType) => {
  let a = calculateLimit(frameWidth, frameHeight, 1, 1);
  const { viewportWidth, viewportHeight } = a;
  if (selectedViewType === 1) {
    a = calculateLimit(viewportWidth, viewportHeight, 2, 2);
  }
  if (selectedViewType === 2) {
    a = calculateLimit(frameWidth, frameHeight, 3, 2);
  }
  if (selectedViewType === 3) {
    a = calculateLimit(viewportWidth, viewportHeight, 3, 3);
  }
  let width1 = a.width;
  let height1 = a.height;
  if (selectedViewType === 4) {
    a = calculateLimit(viewportWidth, viewportHeight, 3, 3);
    width1 = a.width * 2;
    height1 = a.height * 2;
  }
  return {
    width: a.width,
    height: a.height,
    width1,
    height1,
    viewportWidth: a.viewportWidth,
    viewportHeight: a.viewportHeight,
  };
};

export default function CameraBlock(props) {
  const {
    cameraList = [],
  } = useSelector(getCamerasData);
  const { nodeState = {} } = useSelector(getDashboardData);
  const dispatch = useDispatch();
  const { blockRef } = props;
  // const k1 = 0.482; // соотношение размера страницы к размеру блока по дизайну // 0.482
  const k1 = 0.4215; // соотношение размера страницы к размеру блока по дизайну // 0.482
  // const k1 = 0.4315; // соотношение размера страницы к размеру блока по дизайну // 0.482
  const [[width, height], setSize] = useState([window.innerWidth, window.innerHeight]);
  const [fullscreen, setFullscreen] = useState(false);
  const fullscreenWidth = blockRef && blockRef.current && blockRef.current.clientWidth ? blockRef.current.clientWidth : width;
  const fullscreenHeight = blockRef && blockRef.current && blockRef.current.clientHeight ? blockRef.current.clientHeight : height;
  const cameraBlock = useRef(null);
  useEffect(() => {
    const resizeHandler = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);
  const [values, setValues] = useState(null);
  const [animate, setAnimate] = useState(false);
  const [isCamEdit, setCamEdit] = useState(false);
  const [isPlay, setIsPlay] = useState(false);
  const [selectedCameras, setSelectedCameras] = useState([]);
  const intl = useIntl();
  const { formatMessage } = intl;

  useEffect(() => {
    setValues(getAllNodeUiState(nodeState));
  }, [nodeState]);

  const selectCamera = index => id => {
    const list = [...selectedCameras];
    list[index] = id;
    setSelectedCameras(list);
  };
  const dw = width > 1366 ? 32 : 24;
  const dh = width > 1366 ? 82 : 62;
  const [selectedViewType, setSelectedViewType] = useState(0);
  const viewTypes = [
    {
      value: 0, name: '', icon: 'view-type-0', count: 1,
    },
    {
      value: 1, name: '', icon: 'view-type-1', count: 4,
    },
    {
      value: 2, name: '', icon: 'view-type-2', count: 6,
    },
    {
      value: 3, name: '', icon: 'view-type-3', count: 9,
    },
    {
      value: 4, name: '', icon: 'view-type-4', count: 6,
    },
  ];

  const blockWidth = width < 1366 ? 1366 * k1 : width * k1;
  const blockHeight = blockWidth * 0.5625;

  const frameWidth = fullscreen ? fullscreenWidth : blockWidth + dw;
  const frameHeight = fullscreen ? fullscreenHeight : blockHeight + dh;
  const viewType = viewTypes.find(f => f.value === selectedViewType);
  const {
    width: cameraWidth,
    height: cameraHeight,
    width1,
    height1,
    viewportWidth,
    viewportHeight,
  } = calculateCamSize(frameWidth - dw, frameHeight - dh, selectedViewType);

  const fullscreenHandler = () => {
    setAnimate(true);
    setFullscreen(!fullscreen);
    setTimeout(() => {
      setAnimate(false);
    }, 600);
  };

  const createSnapshot = () => {
    const zipArch = new JSZip();
    const promises = [];
    for (let i = 0, len = viewType.count; i < len; i++) {
      const id = selectedCameras[i];
      if (!id) { continue; }
      const p = dispatch(createScreenshot(id)).then(({ data, timestamp }) => {
        if (data) {
          const camera = cameraList.find(f => f.id === id);
          zipArch.file(nameBySave(`${camera.name}_${timestamp}.jpg`), data);
        }
      });
      promises.push(p);
    }

    Promise.all(promises)
      .then(() => {
        zipArch.generateAsync({ type: 'blob' }).then(zipContent => {
          fileDownload(zipContent, 'screenshot.zip');
          successNotification(formatMessage({ id: 'camera.screenshot' }), formatMessage({ id: 'camera.screenshot.success' }));
        });
      })
      .catch(response => {
        errorNotification(formatMessage({ id: 'camera.screenshot' }), getResponseError(response));
      });
  };

  const handleAllNodeEvent = value => {
    dispatch(allNodeEvent(!!value))
      .then(() => {
        setValues(value);
        dispatch(loadNodeState()).catch(() => {});
      })
      .catch(response => {
        errorNotification(formatMessage({ id: value ? 'camera.node.events.unpause' : 'camera.node.events.pause' }), getResponseError(response));
      });
  };

  return (
    <div className={classNames('camera-block', { animate })} style={{ width: `${blockWidth + dw}px`, height: `${blockHeight + dh}px` }}>
      <div className={classNames('camera-container', { frame: fullscreen })} style={{ width: `${frameWidth}px`, height: `${frameHeight}px` }}>
        <div className="camera-header">
          <div className="camera-tab-menu">
            <TabMenu
              selectedTab={values}
              onSelectOptions={item => handleAllNodeEvent(item.value)}
              options={[
                { value: 0, label: formatMessage({ id: 'pause' }) },
                { value: 1, label: formatMessage({ id: 'patrolling' }) },
              ]}
            />
          </div>
          <div className={classNames('control-photo', { disabled: !selectedCameras.length })} onClick={createSnapshot}>
            <Icons customIcon icon="camera" className="icon" />
          </div>
          {/* <div className={classNames('control-play', { disabled: !selectedCameras.length })} onClick={() => { setIsPlay(!isPlay); }}> */}
          {/*  <Icons customIcon icon={isPlay ? 'stop' : 'play'} className="icon" /> */}
          {/* </div> */}
          <div className="control-delimiter" />
          <CameraViewTypeSelector disabled={isCamEdit} selected={selectedViewType} onSelect={setSelectedViewType} list={viewTypes} />
        </div>
        <div className="camera-view">
          <div
            className={classNames('cameras-block', `view-type-${selectedViewType}`)}
            style={{ width: `${viewportWidth}px`, height: `${viewportHeight}px` }}
            ref={cameraBlock}
          >
            {Array(viewType.count).fill(true).map((_, i) => (
              <Camera
                key={i}
                cameraIndex={i}
                cameraList={cameraList}
                cameraWidth={!i && width1 ? width1 : cameraWidth}
                cameraHeight={!i && height1 ? height1 : cameraHeight}
                cameraBlock={cameraBlock}
                selectedViewType={selectedViewType}
                frameWidth={frameWidth}
                frameHeight={frameHeight}
                setCamEdit={setCamEdit}
                selectCamera={selectCamera(i)}
                selectedCameraId={selectedCameras[i]}
                onNodeEvent={handleAllNodeEvent}
                nodeEventValue={values === null ? null : values === 1}
              />
            ))}
          </div>
          <div className="camera-control control-fullscreen" onClick={fullscreenHandler}>
            <Icons customIcon icon={fullscreen ? 'smallscreen' : 'fullscreen'} className="icon" />
          </div>
        </div>
      </div>
    </div>
  );
}
